import * as React from "react";
import { useState } from "react";
import type { VorgangDto } from "../../../services/vorgangService";
import type { AppUser } from "../../../services/userService";
import type { AggregatedNoteDto } from "../../../services/vorgangNoteService";
import { betreffOhneArt } from "../../../data/vorgangArt";
import { slotsFromDto, type CascadeResult } from "../detail/linkSlots";
import type { BeteiligtenFeld } from "../detail/personenZeile";
import { BeteiligteFeld } from "../v3/BeteiligteFeld";
import { BezugsKreise } from "../v3/BezugsKreise";
import { IcoCheck, IcoChevronUnten } from "../v3/vg3Icons";
import type { VerlaufEintrag } from "../v3/verlauf";
import { ArbeitsstrangV4, type StrangHandlers } from "./ArbeitsstrangV4";
import type { VorgangEntwurf } from "../../../services/graphMailService";
import { DatumChipV4 } from "./DatumChipV4";
import { DateiDropZoneV4 } from "./DateiDropZoneV4";
import { PlusMenueV4, type AktionsKnoepfeOptionen } from "./aktionsKnoepfe";
import "../v3/vorgangV3.css";
import "./vorgangV4.css";
import { twvPatch } from "../shared/fristZurTwv";

/**
 * DER VORGANG IN GESTALT EINER TO-DO-KARTE (PO 13./14.09.2026: „vorgang mit
 * todos wo notizen und mails nicht zu todo dann bitte rendern als vorgang
 * gleich erstes todo").
 *
 * Sie erscheint NUR, wenn ein Vorgang To-dos hat UND daneben Eigenes trägt —
 * Mails, Anrufe, Dokumente oder Notizen mit `todoId == null`. Diese Einträge
 * standen bis dahin auf der ganzen Seite nirgends: V4 zeichnet den
 * Vorgangsstrom nur unter `{!hatTodos && …}`. Wann genau sie erscheint, steht
 * in `vorgangAlsStrang.ts`.
 *
 * ══ WARUM EINE EIGENE KOMPONENTE UND NICHT `TodoKarteV4` ═══════════════════
 * Die Karte sieht aus wie ein To-do — sie ist aber KEINS. `TodoKarteV4`
 * speichert über `updateTodo`, `updateTodoStatus`, `updateTodoBeteiligte` und
 * `deleteTodo`. Mit einer VORGANGS-Id gefüttert, schriebe oder löschte sie ein
 * fremdes To-do gleicher Nummer (beide Tabellen haben eigene Id-Folgen). Die
 * Optik ist geteilt (dieselben Klassen), die Schreibwege sind es nicht:
 *
 *   Betreff · TWV · Frist   → `updateVorgang`        (`onPatch`)
 *   Rollen                  → `updateVorgangBeteiligte` / `updateVorgang`
 *                             (`onPatchBeteiligte`, kennt den Sonderweg für
 *                             „zu informieren")
 *   K · R · V               → `vorgangBodyAusPatch`  (`onAnwendenLinks`) —
 *                             NICHT derselbe Rumpf wie beim To-do: der Vorgang
 *                             speichert den Kunden über die `kunde`-Relation.
 *
 * ══ WAS HIER FEHLT, UND WARUM ══════════════════════════════════════════════
 *  · „To-do entfernen" (⋯): löschte den VORGANG. Ersatzlos raus.
 *  · „als erledigt markieren": mit To-dos leitet sich der Vorgangsstatus aus
 *    ihnen ab — `VorgangKopfV4` zeigt den Knopf aus genau diesem Grund nur
 *    unter `{!hatTodos && …}`, und diese Karte gibt es ausschließlich MIT
 *    To-dos. Ein Knopf hier wäre eine zweite, konkurrierende Wahrheit. Der
 *    Status steht deshalb nur als stilles Schild.
 *  · Der Abschluss-Dialog („was kam heraus?") entfällt mit ihm; die
 *    Zusammenfassung des Vorgangs hat ihren eigenen Weg
 *    (`ZusammenfassungKarte` darüber).
 * ═══════════════════════════════════════════════════════════════════════════
 */
type Props = StrangHandlers & {
    vorgang: VorgangDto;
    /** Der Betreff-ENTWURF der Seite — geteilt mit dem Kopf, ein Zustand. */
    titel: string;
    onTitel: (v: string) => void;
    /** Die Einträge am Vorgang selbst (`todoId == null`). */
    eintraege: VerlaufEintrag[];
    alleNotizen: AggregatedNoteDto[];
    users: AppUser[];
    canEdit: boolean;
    aktuellerUserId: string;
    vorgangId: number;
    contactId?: number | null;
    contactName?: string | null;
    vorgangNbr?: string | null;
    vertragNr?: string | null;
    risikoName?: string | null;
    zugewieseneMessageIds: Set<string>;
    /** Schon im Strom liegende Dokumente — die Auswahl graut sie aus. */
    vorhandeneDokumentIds?: ReadonlySet<number>;
    risiken?: Array<{ id: number; label: string }>;
    vertraege?: Array<{ id: number; label: string }>;
    onPatch: (data: Record<string, unknown>) => void;
    onPatchBeteiligte: (feld: BeteiligtenFeld, next: string[]) => void;
    onAnwendenLinks: (result: CascadeResult) => void;
    aktionsOptionen: AktionsKnoepfeOptionen;
    /** Mail-Entwürfe am Vorgang selbst — als Karten im Verlauf; `null` = lädt noch. */
    entwuerfe?: ReadonlyArray<VorgangEntwurf> | null;
};

export const VorgangAlsTodoKarteV4: React.FC<Props> = ({
    vorgang,
    titel,
    onTitel,
    eintraege,
    alleNotizen,
    users,
    canEdit,
    aktuellerUserId,
    vorgangId,
    contactId,
    contactName,
    vorgangNbr,
    vertragNr,
    risikoName,
    zugewieseneMessageIds,
    vorhandeneDokumentIds,
    risiken,
    vertraege,
    onPatch,
    onPatchBeteiligte,
    onAnwendenLinks,
    aktionsOptionen,
    entwuerfe,
    ...handlers
}) => {
    /**
     * AUFGEKLAPPT VON ANFANG AN — anders als eine To-do-Karte.
     *
     * Die Karte gibt es nur, weil sie etwas trägt, das bis heute NIRGENDS zu
     * sehen war. Zugeklappt wäre der Mangel bloß um einen Klick verschoben:
     * wer nicht weiß, dass dort etwas liegt, klappt nicht auf. Das Mockup vom
     * 13.09.2026 zeichnet das erste To-do ebenfalls offen (`chev auf`,
     * `aria-expanded="true"`).
     */
    const [offen, setOffen] = useState(true);
    const erledigt = (vorgang.status ?? "Offen") === "Erledigt";
    // Ein persönlicher Vorgang trägt KEINE Bezüge — das Backend lehnt Kunde,
    // Risiko und Vertrag mit 400 ab („Ein persönlicher Vorgang trägt keine
    // Bezüge.", `VorgangService.update`). Also weder Kreise noch die beiden
    // Zuweisen-Einträge im ⊕.
    const persoenlich = vorgang.persoenlich === true;

    const rolle = (gruppe: BeteiligtenFeld, label: string) => (
        <BeteiligteFeld
            users={users}
            canEdit={canEdit}
            gruppen={[gruppe]}
            persoenlich={persoenlich}
            werte={{
                hauptverantwortlich: vorgang.hauptverantwortlich ?? [],
                bearbeiter: (vorgang.bearbeiter ?? []).map(String),
                zuInformierende: vorgang.zuInformierende ?? [],
            }}
            onChange={(g, next) => onPatchBeteiligte(g, next)}
            popoverTitel={label}
            leerLabel={label}
            mitLegende
            sucheBeiLeerOffen
            // 29 px wie die K·R·V-Kreise in der Zeile darüber (Mockup `.kopf`).
            kreisGroesse={29}
        />
    );

    return (
        /* Dieselben Klassen wie die To-do-Karte: der PO will den Vorgang an
           dieser Stelle ALS erstes To-do sehen, nicht als dritte Kartensorte.
           `vg4-vorgangskarte` trägt nichts Optisches — sie ist der Griff, an
           dem Tests und spätere Sonderregeln die Karte fassen können. */
        <DateiDropZoneV4
            className={`vg4-card vg4-todo vg4-vorgangskarte${offen ? " open" : ""}${erledigt ? " done" : ""}`}
            vorgangId={vorgangId}
            zielName={titel || vorgang.vorgangNbr || `Vorgang ${vorgangId}`}
            canEdit={canEdit}
            onGeaendert={handlers.onGeaendert}
            data-testid="vorgangskarte"
        >
            <div className="vg4-z3">
                <button
                    type="button"
                    className={`vg4-chev${offen ? " open" : ""}`}
                    aria-expanded={offen}
                    aria-label={offen ? "Vorgang zuklappen" : "Vorgang aufklappen"}
                    title={offen ? "Vorgang zuklappen" : "Vorgang aufklappen"}
                    onClick={() => setOffen((o) => !o)}
                >
                    <IcoChevronUnten size={15} />
                </button>

                {/* Ein Eingabefeld wie im Kopf, kein Knopf wie am To-do: der
                    ENTWURF gehört der Seite und ist derselbe, den der Kopf
                    zeigt, solange er den Betreff trägt. Zwei Entwürfe für einen
                    Wert liefen auseinander, sobald beide gleichzeitig stünden. */}
                <input
                    className="vg4-betreff"
                    value={titel}
                    disabled={!canEdit}
                    aria-label="Betreff des Vorgangs"
                    placeholder="Betreff…"
                    onChange={(e) => onTitel(e.target.value)}
                    onBlur={() => {
                        // Ein geleerter Betreff ist erlaubt: das Backend setzt
                        // bei NKG/JG/GAB dann das Kürzel zurück. Verglichen
                        // wird mit dem GESPEICHERTEN Wert — sonst schriebe
                        // jedes Verlassen des Felds.
                        const t = titel.trim();
                        if (t !== betreffOhneArt(vorgang.vorgangsart, vorgang.titel)) onPatch({ titel: t });
                        else onTitel(betreffOhneArt(vorgang.vorgangsart, vorgang.titel));
                    }}
                />

                <div className="vg4-z3-r">
                    {persoenlich && <span className="vg3-badge">persönlich</span>}

                    {!persoenlich && (
                        <BezugsKreise
                            slots={slotsFromDto(vorgang)}
                            canEdit={canEdit}
                            onApply={onAnwendenLinks}
                            gegenstand="des Vorgangs"
                        />
                    )}

                    <PlusMenueV4
                        canEdit={canEdit}
                        slots={slotsFromDto(vorgang)}
                        onAnwendenLinks={onAnwendenLinks}
                        persoenlich={persoenlich}
                        {...aktionsOptionen}
                    />

                    {/* NUR LESEN. Der Status des Vorgangs leitet sich mit
                        To-dos aus ihnen ab; ein Knopf hier kämpfte gegen die
                        Ableitung. Offen steht gar nichts da — ein Schild
                        „offen" an jeder Karte wäre Rauschen. */}
                    {erledigt && (
                        <span
                            className="vg4-status-schild"
                            title="Der Vorgang gilt als erledigt, sobald seine To-dos es sind."
                        >
                            <span className="vg3-check done" aria-hidden>
                                <IcoCheck size={11} />
                            </span>
                            erledigt
                        </span>
                    )}
                </div>
            </div>

            <div className="vg4-z2">
                <div className="vg4-fristen">
                    <span className="vg4-frist">
                        <span className="vg4-frist-label">TWV</span>
                        <DatumChipV4
                            label="Wiedervorlage"
                            value={vorgang.twv}
                            canEdit={canEdit}
                            erledigt={erledigt}
                            onSave={(v) => onPatch(twvPatch(v, vorgang.frist))}
                        />
                    </span>
                    <span className="vg4-frist">
                        <span className="vg4-frist-label">Frist</span>
                        <DatumChipV4
                            label="Frist"
                            value={vorgang.frist}
                            canEdit={canEdit}
                            erledigt={erledigt}
                            onSave={(v) => onPatch({ frist: v })}
                        />
                    </span>
                </div>

                <div className="vg4-rollen">
                    {rolle("hauptverantwortlich", "Hauptverantwortlich")}
                    {rolle("bearbeiter", "Bearbeiter")}
                    {rolle("zuInformierende", "zu informieren")}
                </div>
            </div>

            {/* Hier stand bis zum 21.09.2026 „1 Notiz · 3 im Verlauf"
                (PO 15.09.2026), ersatzlos raus (PO 21.09.2026) — Begründung
                an derselben Stelle in `TodoKarteV4.tsx`. */}
            {offen && (
                /* OHNE `todoId` — das ist der ganze Punkt: die Notizfläche
                   filtert damit auf `todoId === null` und zeichnet endlich die
                   Blöcke, die am Vorgang hängen. `eintraege` sind die eigenen
                   Einträge der Seite. */
                <ArbeitsstrangV4
                    {...handlers}
                    {...(entwuerfe !== undefined ? { entwuerfe } : {})}
                    eintraege={eintraege}
                    alleNotizen={alleNotizen}
                    users={users}
                    canEdit={canEdit}
                    aktuellerUserId={aktuellerUserId}
                    vorgangId={vorgangId}
                    contactId={contactId}
                    contactName={contactName}
                    vorgangNbr={vorgangNbr}
                    vertragNr={vertragNr}
                    risikoName={risikoName}
                    zugewieseneMessageIds={zugewieseneMessageIds}
                    vorhandeneDokumentIds={vorhandeneDokumentIds}
                    risiken={risiken}
                    vertraege={vertraege}
                />
            )}
        </DateiDropZoneV4>
    );
};
